import qs from 'query-string'

export function selectPromoKey(routeKey: unknown): string {
    return String(routeKey || '').trim()
}

try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('dh.promo.key')
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('dh.promo.key')
} catch {
    // ignore quota / private mode / missing storage
}

/** Same-origin path only: blocks protocol-relative and absolute URLs. */
export function getSafeInternalPath(path: unknown): string | null {
    const raw = Array.isArray(path) ? path[0] : path
    if (typeof raw !== 'string') return null
    const trimmed = raw.trim()
    if (!trimmed.startsWith('/') || trimmed.startsWith('//') || trimmed.includes('://')) {
        return null
    }
    return trimmed
}

/**
 * ChatButton / popup already pass fs=1 so mobile (≤760) opens fullscreen.
 * Promo redirects to GCB2 should do the same.
 */
export function withChatFullscreenQuery(path: string): string {
    if (!path.includes('/GCB2')) return path
    if (/(?:^|[?&])fs=/.test(path)) return path
    return path.includes('?') ? `${path}&fs=1` : `${path}?fs=1`
}

/** Survives a browser hop (Telegram and similar) because it lives in the URL. */
export function isMultichatChatPath(path: string): boolean {
    return /[?&]section=chat(?:&|$)/.test(path || '')
}

export function withOpenSmsPromoQuery(path: string): string {
    if (!isMultichatChatPath(path)) return path
    if (/(?:^|[?&])openSmsPromo=/.test(path)) return path
    return path.includes('?') ? `${path}&openSmsPromo=1` : `${path}?openSmsPromo=1`
}

function omitToQueryParam(
    query: { to?: unknown; [key: string]: unknown } | null | undefined
): Record<string, unknown> {
    if (!query || typeof query !== 'object') return {}
    const rest = { ...query }
    delete rest.to
    return rest
}

const MARKETPLACE_INIT_CONNECTORS = new Set(['yClients', 'dikidi'])

/** `/init/yClients` or `/init/dikidi` only — not `/connect` (auth required). */
export function isMarketplaceInitPath(path: unknown): boolean {
    if (typeof path !== 'string') return false
    const match = path.match(/^\/init\/([^/]+)$/)
    return Boolean(match && MARKETPLACE_INIT_CONNECTORS.has(match[1]))
}

function mergeToPathWithSiblingQuery(
    query: { to?: unknown; [key: string]: unknown } | null | undefined
): { url: string; path: string } | null {
    const returnPath = getSafeInternalPath(query?.to)
    if (!returnPath) return null
    const parsed = qs.parseUrl(returnPath)
    return {
        url: parsed.url,
        path: qs.stringifyUrl({
            url: parsed.url,
            query: { ...omitToQueryParam(query), ...parsed.query } as qs.ParsedQuery,
        }),
    }
}

/**
 * Where to send an already-authenticated user who landed on /landing or /.
 * Follow `to` when present (Telegram handoff, marketplace, billing).
 * Otherwise /projects — do not resume promo from a leftover client key.
 * Sibling query (fastReg, salon_id, section, …) must travel with `to`.
 */
export function resolveAuthenticatedLandingRedirect(
    query: { to?: unknown; [key: string]: unknown } | null | undefined
): string {
    const merged = mergeToPathWithSiblingQuery(query)
    if (merged) return merged.path
    return '/projects'
}

/**
 * Guest MP entry: `/?to=/init/yClients&fastReg=…` must reach the unprotected
 * init route so fastRegister can run. Do not follow arbitrary `to` (promo
 * security) or `/init/.../connect` (needs a session).
 */
export function resolveGuestMarketplaceInitRedirect(
    query: { to?: unknown; [key: string]: unknown } | null | undefined
): string | null {
    const merged = mergeToPathWithSiblingQuery(query)
    if (!merged || !isMarketplaceInitPath(merged.url)) return null
    return merged.path
}

/**
 * Short resolve redirectPath (`/GCB2?section=chat&promo=…`) + chosen project id.
 * Leaves an already project-scoped path unchanged.
 */
export function withProjectPrefix(projectId: string, redirectPath: string): string {
    const id = String(projectId || '').trim()
    const path = String(redirectPath || '').trim()
    if (!id || !path.startsWith('/') || path.startsWith('//') || path.includes('://')) {
        return path
    }
    if (path === '/') {
        return `/project/${id}`
    }
    if (path === `/project/${id}` || path.startsWith(`/project/${id}/`) || path.startsWith(`/project/${id}?`)) {
        return path
    }
    if (/^\/project\/[^/]+/.test(path)) {
        return path
    }
    return `/project/${id}${path}`
}
